/**
 * Theme Store
 *
 * Zustand store for managing theme state (light/dark mode + advanced customization)
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemeMode = 'light' | 'dark' | 'system';

export interface ThemePreset {
  id: string;
  name: string;
  description?: string;
  mode: ThemeMode;
  accentColor: string;
  highContrast: boolean;
}

export interface ThemeCustomization {
  accentColor: string;
  highContrast: boolean;
}

interface ThemeStore {
  // State
  mode: ThemeMode;
  resolvedTheme: 'light' | 'dark'; // Actual theme after resolving 'system'
  accentColor: string; // Custom accent color (hex)
  highContrast: boolean; // High contrast mode for accessibility
  currentPreset: string | null; // ID of active preset (null = custom)

  // Actions
  setTheme: (mode: ThemeMode) => void;
  toggleTheme: () => void;
  setAccentColor: (color: string) => void;
  setHighContrast: (enabled: boolean) => void;
  applyPreset: (preset: ThemePreset) => void;
  exportTheme: () => string; // Export as JSON string
  importTheme: (json: string) => void; // Import from JSON string
  resetToDefaults: () => void;
  initializeTheme: () => void;
}

// Helper: Get system theme preference
const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window === 'undefined') return 'light';

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  return mediaQuery.matches ? 'dark' : 'light';
};

// Helper: Resolve theme mode to actual theme
const resolveTheme = (mode: ThemeMode): 'light' | 'dark' => {
  if (mode === 'system') {
    return getSystemTheme();
  }
  return mode;
};

// Helper: Apply theme customization to document
const applyThemeCustomization = (
  resolvedTheme: 'light' | 'dark',
  accentColor: string,
  highContrast: boolean
) => {
  // Update document class for CSS
  document.documentElement.classList.remove('light', 'dark', 'high-contrast');
  document.documentElement.classList.add(resolvedTheme);
  if (highContrast) {
    document.documentElement.classList.add('high-contrast');
  }

  // Update document attributes
  document.documentElement.setAttribute('data-theme', resolvedTheme);
  document.documentElement.setAttribute('data-high-contrast', String(highContrast));

  // Update CSS custom property for accent color
  document.documentElement.style.setProperty('--accent-color', accentColor);
};

// Default values
const DEFAULT_ACCENT_COLOR = '#1890ff';
const DEFAULT_MODE: ThemeMode = 'system';
const DEFAULT_HIGH_CONTRAST = false;

const useThemeStore = create<ThemeStore>()(
  persist(
    (set, get) => ({
      // Initial state
      mode: DEFAULT_MODE,
      resolvedTheme: 'light',
      accentColor: DEFAULT_ACCENT_COLOR,
      highContrast: DEFAULT_HIGH_CONTRAST,
      currentPreset: null,

      // Set theme mode
      setTheme: (mode: ThemeMode) => {
        const resolved = resolveTheme(mode);
        const { accentColor, highContrast } = get();

        // Update state
        set({ mode, resolvedTheme: resolved, currentPreset: null });

        // Apply to document
        applyThemeCustomization(resolved, accentColor, highContrast);
      },

      // Toggle between light and dark
      toggleTheme: () => {
        const { mode } = get();

        // If in system mode, toggle to opposite of current system preference
        if (mode === 'system') {
          const systemTheme = getSystemTheme();
          const newMode = systemTheme === 'light' ? 'dark' : 'light';
          get().setTheme(newMode);
        } else {
          // Toggle between light and dark
          const newMode = mode === 'light' ? 'dark' : 'light';
          get().setTheme(newMode);
        }
      },

      // Set custom accent color
      setAccentColor: (color: string) => {
        const { resolvedTheme, highContrast } = get();
        set({ accentColor: color, currentPreset: null });
        applyThemeCustomization(resolvedTheme, color, highContrast);
      },

      // Toggle high contrast mode
      setHighContrast: (enabled: boolean) => {
        const { resolvedTheme, accentColor } = get();
        set({ highContrast: enabled, currentPreset: null });
        applyThemeCustomization(resolvedTheme, accentColor, enabled);
      },

      // Apply a theme preset
      applyPreset: (preset: ThemePreset) => {
        const resolved = resolveTheme(preset.mode);
        set({
          mode: preset.mode,
          resolvedTheme: resolved,
          accentColor: preset.accentColor,
          highContrast: preset.highContrast,
          currentPreset: preset.id,
        });
        applyThemeCustomization(resolved, preset.accentColor, preset.highContrast);
      },

      // Export theme as JSON
      exportTheme: () => {
        const { mode, accentColor, highContrast } = get();
        const theme = {
          version: 1,
          mode,
          accentColor,
          highContrast,
          exportedAt: new Date().toISOString(),
        };
        return JSON.stringify(theme, null, 2);
      },

      // Import theme from JSON
      importTheme: (json: string) => {
        try {
          const theme = JSON.parse(json);

          // Validate
          if (!theme.version || theme.version !== 1) {
            throw new Error('Invalid theme version');
          }

          // Apply imported theme
          const mode = theme.mode as ThemeMode;
          const resolved = resolveTheme(mode);

          set({
            mode,
            resolvedTheme: resolved,
            accentColor: theme.accentColor || DEFAULT_ACCENT_COLOR,
            highContrast: theme.highContrast || false,
            currentPreset: null,
          });

          const { accentColor, highContrast } = get();
          applyThemeCustomization(resolved, accentColor, highContrast);
        } catch (error) {
          console.error('Failed to import theme:', error);
          throw new Error('Invalid theme configuration');
        }
      },

      // Reset to default theme
      resetToDefaults: () => {
        const resolved = resolveTheme(DEFAULT_MODE);
        set({
          mode: DEFAULT_MODE,
          resolvedTheme: resolved,
          accentColor: DEFAULT_ACCENT_COLOR,
          highContrast: DEFAULT_HIGH_CONTRAST,
          currentPreset: null,
        });
        applyThemeCustomization(resolved, DEFAULT_ACCENT_COLOR, DEFAULT_HIGH_CONTRAST);
      },

      // Initialize theme from localStorage or system preference
      initializeTheme: () => {
        const { mode, accentColor, highContrast } = get();
        const resolved = resolveTheme(mode);

        set({ resolvedTheme: resolved });
        applyThemeCustomization(resolved, accentColor, highContrast);

        // Listen for system theme changes
        if (typeof window !== 'undefined') {
          const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

          const handleChange = (e: MediaQueryListEvent) => {
            const { mode, accentColor, highContrast } = get();
            if (mode === 'system') {
              const resolved = e.matches ? 'dark' : 'light';
              set({ resolvedTheme: resolved });
              applyThemeCustomization(resolved, accentColor, highContrast);
            }
          };

          // Modern browsers
          if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleChange);
          } else {
            // Fallback for older browsers
            mediaQuery.addListener(handleChange);
          }
        }
      },
    }),
    {
      name: 'theme-preferences', // localStorage key
      // Persist all theme settings
      partialize: (state) => ({
        mode: state.mode,
        accentColor: state.accentColor,
        highContrast: state.highContrast,
        currentPreset: state.currentPreset,
      }),
    }
  )
);

export default useThemeStore;
